<script setup>
import { 
	ref, 
	watch,
	provide,
	onMounted, 
} from 'vue'

import { useRoute, useRouter } from 'vue-router'
import SaveBtn from '@/components/layout/SaveBtn.vue'
import BaseForm from '@/components/autoform/BaseForm.vue'
import NavBarActions from '@/components/layout/NavBarActions'
import { VehicleService } from '@/services/vehicle-service'
import { InspectionService } from '@/services/inspection-service'
import { useVehicleStore, storeReady as vStoreReady } from '@/stores/vehicle-store'
import { useInspectionStore, storeReady as iStoreReady } from '@/stores/inspection-store'
import { Log } from '@/modules/util/log'
import { Settings } from '@/settings'
import { traverse } from '@/modules/util/object'
import { NAMA } from '@/modules/nama/main'
import { HTTP } from '@/services/http'
import { Notify } from '@/modules/util/notify'

const vehicleService = new VehicleService
const inspectionService = new InspectionService
const route = useRoute()
const router = useRouter()
const vehicleStore = useVehicleStore()
const inspectionStore = useInspectionStore()
const { INSPECTION_TYPES } = Settings

const loading = ref(true)
const submitting = ref(false)
const config = ref(null)
const data = ref({})
const form = ref(null)
const fetchers = []

const getVehicle = async () => {
	const promise = vehicleService.get(route.params.id).then(res => {
		if (!(res.body?.status && res.body?.vehicle)) {
			throw new Error('Failed to fetch data')
		}

		const { vehicle } = res.body

		if (
			vehicleStore.currentVehicle &&
			String(vehicleStore.currentVehicle?.id) !== String(vehicle.id)
		) {
			return
		}

		data.value = {
			...data.value,
			vehicle: {
				...vehicle,
				...data.value.vehicle,
			},
		}

		vehicleStore.setCurrentVehicle(data.value.vehicle)
		return vehicle
	}).catch(e => {
		Log.warning(
			'Could not fetch latest vehicle data. You may have poor connectivity.',
			e.message || e,
		)
	})

	await vStoreReady

	const item = vehicleStore.currentVehicle || await promise    
	data.value = {
		...data.value,
		vehicle: {
			...(item || {}),
			...data.value.vehicle,
		}
	}
    
	return item 
}

const listInspectors = async () => {
	const promise = inspectionService.inspectors().then(res => {
		if (!(res.body?.status && res.body?.inspectors)) {
			throw new Error('Failed to fetch data')
		}

		inspectionStore.setInspectors(res.body.inspectors)
		return res.body.inspectors
	}).catch(e => {
		Log.warning(
			'Could not refresh inspector list. You may have poor connectivity.',
			e.message || e,
		)
	})

	return inspectionStore.allInspectors || await promise
}

const listTyreManufacturers = async () => {
	const promise = vehicleService.tyreManufacturers().then(res => {
		if (!(res.body?.status && res.body?.manufacturers)) {
			throw new Error(JSON.stringify(res.body))
		}

		vehicleStore.setTyreManufacturers(res.body.manufacturers)
		return res.body.manufacturers
	}).catch(e => {
		Log.warning(
			'Could not refresh tyre manufacturers. You may have poor connectivity.',
			e.message || e,
		)
	})

	await vStoreReady

	return vehicleStore.allTyreManufacturers || await promise
}

const listBodyBuilders = async () => {
	const promise = vehicleService.bodyBuilders().then(res => {
		if (!(res.body?.status && res.body?.bodybuilders)) {
			throw new Error(JSON.stringify(res.body))
		}

		vehicleStore.setBodyBuilders(res.body.bodybuilders)
		return res.body.bodybuilders
	}).catch(e => {
		Log.warning(
			'Could not refresh body builders. You may have poor connectivity.',
			e.message || e,
		)
	})

	await vStoreReady

	const item = vehicleStore.allBodyBuilders || await promise
	item.push({ name: '__other', title: 'Other...' })

	return item
}

const listBodyStyles = async () => {
	const promise = vehicleService.bodyStyles().then(res => {
		if (!(res.body?.status && res.body?.bodystyles)) {
			throw new Error(JSON.stringify(res.body))
		}

		vehicleStore.setBodyStyles(res.body.bodystyles)
		return res.body.bodystyles
	}).catch(e => {
		Log.warning(
			'Could not refresh body builders. You may have poor connectivity.',
			e.message || e,
		)
	})

	await vStoreReady

	const item = vehicleStore.allBodyStyles || await promise
	item.push({ name: '__other', title: 'Other...' })

	return item
}

const listCabTypes = async () => {
	const promise = vehicleService.cabTypes().then(res => {
		if (!(res.body?.status && res.body?.cabtypes)) {
			throw new Error(JSON.stringify(res.body))
		}

		vehicleStore.setCabTypes(res.body.cabtypes)
		return res.body.cabtypes
	}).catch(e => {
		Log.warning(
			'Could not refresh body builders. You may have poor connectivity.',
			e.message || e,
		)
	})

	await vStoreReady

	const item = vehicleStore.allCabTypes || await promise
	item.push({ name: '__other', title: 'Other...' })

	return item
}

const listFeatures = async () => {
	const promise = vehicleService.features(route.params.id).then(res => {
		if (!(res.body?.status && res.body?.features)) {
			throw new Error(JSON.stringify(res.body))
		}

		vehicleStore.setFeatures(res.body.features)
		
		return res.body.features
	}).catch(e => {
		Log.warning(
			'Could not refresh inspection features. You may have poor connectivity.',
			e.message || e,
		)
	})

	await vStoreReady

	return vehicleStore.allFeatures || await promise
}

const listGrades = async () => {
	const promise = vehicleService.grades().then(res => {		
		if (!(res.body?.status && res.body?.grades)) {
			throw new Error(JSON.stringify(res.body))
		}

		vehicleStore.setGrades(res.body.grades)
		return res.body.grades
	}).catch(e => {
		Log.warning(
			'Could not refresh vehicle grades. You may have poor connectivity.',
			e.message || e,
		)
	})

	await vStoreReady

	return vehicleStore.allGrades || await promise
}

const listNamaGrades = async () => {
	const grades = (await listGrades())['nama']

	if (grades.find(val => val.motorvehiclegrade_value === 'AUTO')) {
		return grades
	}
    
	grades.unshift({ 
		motorvehiclegrade_label: 'Auto', 
		motorvehiclegrade_value: 'AUTO' 
	}, {
		motorvehiclegrade_label: 'Do not grade', 
		motorvehiclegrade_value: 'NOGRADE' 
	})

	return grades
}

const listExteriorGrades = async () => {
	return (await listGrades())['exterior']
}

const listInteriorGrades = async () => {
	return (await listGrades())['interior']
}

const initNAMA = async () => {
	const lcv = data.value.vehicle?.classification_id == '2' 
		|| route.params.typeId == '10'

	let gradevalues = inspectionStore.allNamaGradeValues
	let thresholds = inspectionStore.allNamaGradeThresholds

	const init = () => {
		NAMA.init(
			gradevalues, 
			thresholds, 
			inspectionStore.currentDamageItems,
			lcv,
		)
		inspectionStore.setNamaGrade(NAMA.grade())
	}
    
	const promise = inspectionService.namaGradeValues().then(res => {
		if (!res.body?.status) {
			throw new Error(JSON.stringify(res.body))
		}

		gradevalues = res.body.gradevalues
		thresholds = res.body.thresholds

		inspectionStore.setNamaGradeValues(gradevalues)
		inspectionStore.setNamaGradeThresholds(thresholds)

		init()
	}).catch(e => {
		console.warn('initNAMA:', e.message || e)
	})

	if (!(gradevalues && thresholds))
		await promise

	if (!(gradevalues && thresholds)) {
		Notify.error(
			'Failed to load NAMA grade data from network or cache.' +
				'Please initialize this inspection type with a network connection.',
		)
		router.push({ name: 'get-vehicle', params: { id: route.params.id } })
	}

	init()
}

const saveInspection = async () => {
	await form.value.validate()

	if (!form.value.valid || submitting.value)
		return false

	submitting.value = true
	data.value.inspection.saved_datetime = new Date().toISOString()

	// Decouple data from Vue's reactivity immediately
	const payload = JSON.parse(JSON.stringify(data.value))

	try {
		if (payload.inspection) {
			vehicleStore.tyreTypes.forEach(type => {
				if (!(payload.inspection[type] && payload.inspection[type].treaddepth)) {
					return
				}

				const treadParts = payload.inspection[type].treaddepth.split('.')
				payload.inspection[type].treaddepthinteger = treadParts[0]
				payload.inspection[type].treaddepthfractional = treadParts[1]
				
				delete payload.inspection[type].treaddepth
			})
		}

		inspectionService.save(
			route.params.id, 
			route.params.typeId,
			payload,
		).then(res => {
			if (!res.uid) {
				throw new Error(JSON.stringify(res))
			}
		})
	} catch (e) {
		Notify.error('Failed to save inspection')
		console.error('saveInspection:', e.message || e, data.value.vehicle ? data.value.vehicle.registration : '')
	} finally {
		Settings.clearProgress()
		inspectionStore.setCurrentInspection(null)
		inspectionStore.setDamageItems([])
    
		router.push({ name: 'get-vehicle', params: { id: route.params.id } })
	}

	return true
}

provide('saveInspection', saveInspection)

const getRequestedInspection = async () => {
	let inspection = null

	if (route.params.inspectionId) {
		inspection = (inspectionStore.allInspections || [])
			.find(val => val.motorvehicleinspection_id == route.params.inspectionId)
			?.motorvehicleinspection_data
	}

	return inspection
}

const getInProgressInspection = async () => {
	let inspection = null

	const cached = Settings.getProgress() || {}

	if (
		cached.vehicleId == route.params.id && 
		cached.inspectionId == route.params.typeId
	) {
		// restore progress
		inspection = cached.data.inspection
		if (cached.data.vehicle) {
			data.value.vehicle = cached.data.vehicle
		}
	} else {
		// clear up cached images we no longer need
		await traverse(cached?.data || {}, (key, value) => {
			if (!value?.uid) {
				return
			}

			HTTP.deleteCached(value.uid)
		})
	}

	return inspection
}

const getInspection = async () => {
	let inspection = await getRequestedInspection() 
		|| await getInProgressInspection()

	if (inspection) {
		vehicleStore.tyreTypes.forEach(type => {
			if (!(
				inspection[type] 
				&& (inspection[type].treaddepthinteger 
					|| inspection[type].treaddepthfractional) 
				&& !inspection[type].treaddepth
			)) {
				return
			}

			let int = inspection[type].treaddepthinteger
			let frac = inspection[type].treaddepthfractional

			int = int ? Number(int) : 0
			frac = frac ? Number(frac) : 0

			inspection[type].treaddepth = `${int}.${frac}`
		})
	} else {
		inspection = {}
	}

	if (!inspection.damageitems)
		inspection.damageitems = []

	data.value.inspection = inspection
	inspectionStore.setCurrentInspection(inspection)
	inspectionStore.setDamageItems(inspection.damageitems)
}

const setInspectionType = async () => {
	const type = INSPECTION_TYPES.filter(val => val.id == route.params.typeId)[0]
	config.value = type.data.config

	const fMap = {
		listInspectors,
		listTyreManufacturers,
		listBodyBuilders,
		listBodyStyles,
		listCabTypes,
		listFeatures,
		listNamaGrades,
		listExteriorGrades,
		listInteriorGrades,
		listUpcomingAuctions,
	}

	await traverse(config.value, (key, value) => {
		if (key !== 'fetcher')
			return true

		if (!fMap[value])
			throw new Error(`Unknown fetcher specified in config: ${value}`)

		fetchers.push(fMap[value])
		return false
	})
}

const cacheProgress = () => {
	Settings.cacheProgress(route.params.id, route.params.typeId, data.value)
}

const onReady = () => {
	HTTP.initCacheRetry()
	inspectionStore.currentDamageItems.forEach(NAMA.add)
}

const updateTyreDamageItem = (component, condition) => {
	const conditions = {
		'Damaged': true,
		'Missing': true,
	}

	// When loading in an existing inspection, damage item may already exist
	let existing 
	const items = inspectionStore.currentDamageItems.filter(d => {
		if (d.component !== component)
			return true
		existing = d
		return false
	})

	if (conditions[condition]) {
		items.push(existing || {
			damagemappos: { x: null, y: null },
			date: new Date().toLocaleString('en-GB'),
			zone: 'Tyres',
			category: 'Tyres',
			component,
			condition,
			severity: 'Replace',
			repair: '',
			image: null,
			imagename: null
		})
	}

	inspectionStore.setDamageItems(items)
	data.value.inspection.damageitems = items
}

if (INSPECTION_TYPES.find(val => val.id == route.params.typeId)?.name != 'motorbike') {
	Array('OSF', 'NSF', 'OSR', 'NSR').forEach(position => {
		watch(
			() => data.value?.inspection?.[`Tyre${position}`]?.condition,
			condition => updateTyreDamageItem(`Tyre ${position.toLowerCase()}`, condition),
		)
	})
}

const listUpcomingAuctions = async () => {
	const res = await vehicleService.upcomingAuctions()
	if (!(res.body?.status && res.body?.body && typeof res.body.body === 'object')) {
		throw new Error('Bad response')
	}
	return res.body.body
}

onMounted(async () => {
	await iStoreReady
	await vStoreReady

	await getVehicle()
	await setInspectionType()
	await getInspection()
	await initNAMA()

	loading.value = false
})

</script>

<template>
<BaseForm
	v-if="!loading"
	ref="form"
	v-model="data"
	:config="config"
	:fetchers="fetchers"
	@ready="onReady"
	@submit="saveInspection"
	@update="cacheProgress"
/>

<NavBarActions>
	<div class="d-flex flex-row">
		<v-btn
			v-show="data.inspection && data.inspection.namagrade === 'AUTO'"
			variant="tonal"
			class="mr-3 nama"
		>
			<span>NAMA</span> {{ inspectionStore.currentNamaGrade }}
		</v-btn>
		<SaveBtn
			:loading="submitting"
			:base-form="form"
		/>
	</div>
</NavBarActions>
</template>

<style lang="scss" scoped>
.nama {
	pointer-events: none;
	font-weight: 900;

	span {
		letter-spacing: .25rem;
		font-weight: normal;
		margin-right: .25rem;
	}
}
</style>